import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axiosInstance from '@/config/axiosInstance';
import { endpoints } from '@/config/apiEndpoints';
import { detectUserTimezone, normalizeTimezone } from '@/utils/timezoneDetection';
import { Sidebar } from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  User,
  Shield,
  Check,
  Crown,
  Calendar,
  Database,
  Eye,
  EyeOff,
  Building2,
  Camera,
  Upload,
  Loader2,
  Rocket,
  Repeat2,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  X,
  Info,
  AlertTriangle, Clock, XCircle, ArrowRight,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { useTimezone } from '@/contexts/TimezoneContext';
import Pricing from '@/pages/Pricing';
import { PageHeader } from "@/components/Header";
import BotLauncher from "@/components/BotLauncher";

// ─────────────────────────────────────────────────────────────────────────────
// IMAGE CROP MODAL
// ─────────────────────────────────────────────────────────────────────────────

const PROFILE_CROP_W = 150;
const PROFILE_CROP_H = 180;
const LOGO_CROP_W = 180;
const LOGO_CROP_H = 180;

const normalizeOptionalText = (value: unknown) => {
  if (value === null || value === undefined) return '';

  const text = String(value).trim();
  if (!text || ['none', 'null', 'undefined','N/A','n/a'].includes(text.toLowerCase())) {
    return '';
  }

  return text;
};

interface CropModalProps {
  open: boolean;
  imageUrl: string;
  title?: string;
  outputSize?: number;
  cropW: number;
  cropH: number;
  onConfirm: (croppedDataUrl: string) => void;
  onCancel: () => void;
}

const ImageCropModal: React.FC<CropModalProps> = ({
  open,
  imageUrl,
  title = 'Adjust Image',
  outputSize = 400,
  cropW,
  cropH,
  onConfirm,
  onCancel,
}) => {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [imgLoaded, setImgLoaded] = useState(false);
  const [minScale, setMinScale] = useState(1);

  const cropContainerRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const dragging = useRef(false);
  const lastPointer = useRef({ x: 0, y: 0 });
  const lastPinchDist = useRef<number | null>(null);
  const scaleRef = useRef(1);
  const naturalRef = useRef({ w: 1, h: 1 });
  const loadedUrlRef = useRef('');

  /* ── helpers ── */
  const getCoverScale = (w: number, h: number) =>
    Math.max(cropW / w, cropH / h);

  const clampOff = (ox: number, oy: number, sc: number, w: number, h: number) => {
    const iw = w * sc;
    const ih = h * sc;
    const maxX = Math.max(0, (iw - cropW) / 2);
    const maxY = Math.max(0, (ih - cropH) / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, ox)),
      y: Math.max(-maxY, Math.min(maxY, oy)),
    };
  };

  const applyInit = (w: number, h: number) => {
    const cover = Math.max(cropW / w, cropH / h);
    naturalRef.current = { w, h };
    scaleRef.current = cover;
    setMinScale(cover);
    setScale(cover);
    setOffset({ x: 0, y: 0 });
    setImgLoaded(true);
  };

  /* ── when modal opens, load image via JS Image() for reliable dimensions ── */
  useEffect(() => {
    if (!open || !imageUrl) return;
    if (loadedUrlRef.current === imageUrl) return;

    setImgLoaded(false);
    loadedUrlRef.current = imageUrl;

    const probe = new window.Image();
    probe.onload = () => applyInit(probe.naturalWidth, probe.naturalHeight);
    probe.onerror = () => console.error('CropModal: image failed to load');
    probe.src = imageUrl;
  }, [open, imageUrl]);

  /* ── reset when modal fully closes ── */
  useEffect(() => {
    if (!open) {
      loadedUrlRef.current = '';
      setImgLoaded(false);
      setScale(1);
      setMinScale(1);
      setOffset({ x: 0, y: 0 });
      naturalRef.current = { w: 1, h: 1 };
      scaleRef.current = 1;
    }
  }, [open]);

  /* ── wheel zoom ── */
  useEffect(() => {
    const el = cropContainerRef.current;
    if (!el || !imgLoaded) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const { w, h } = naturalRef.current;
      const cover = getCoverScale(w, h);
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setScale(prev => {
        const next = Math.max(cover, Math.min(5, prev + delta));
        scaleRef.current = next;
        setOffset(o => clampOff(o.x, o.y, next, w, h));
        return next;
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [imgLoaded]);

  /* ── zoom buttons ── */
  const resetTransform = () => {
    const { w, h } = naturalRef.current;
    const cover = getCoverScale(w, h);
    scaleRef.current = cover;
    setScale(cover);
    setOffset({ x: 0, y: 0 });
  };

  const changeZoom = (delta: number) => {
    const { w, h } = naturalRef.current;
    const cover = getCoverScale(w, h);
    setScale(prev => {
      const next = Math.max(cover, Math.min(5, prev + delta));
      scaleRef.current = next;
      setOffset(o => clampOff(o.x, o.y, next, w, h));
      return next;
    });
  };

  /* ── pointer drag ── */
  const onPointerDown = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragging.current = true;
    lastPointer.current = { x: e.clientX, y: e.clientY };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    const dx = e.clientX - lastPointer.current.x;
    const dy = e.clientY - lastPointer.current.y;
    lastPointer.current = { x: e.clientX, y: e.clientY };
    const { w, h } = naturalRef.current;
    setOffset(prev => clampOff(prev.x + dx, prev.y + dy, scaleRef.current, w, h));
  };
  const onPointerUp = () => { dragging.current = false; };

  /* ── pinch zoom ── */
  const onTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length !== 2) return;
    const dx = e.touches[0].clientX - e.touches[1].clientX;
    const dy = e.touches[0].clientY - e.touches[1].clientY;
    const dist = Math.hypot(dx, dy);
    if (lastPinchDist.current !== null) {
      const { w, h } = naturalRef.current;
      const cover = getCoverScale(w, h);
      const ratio = dist / lastPinchDist.current;
      setScale(prev => {
        const next = Math.max(cover, Math.min(5, prev * ratio));
        scaleRef.current = next;
        setOffset(o => clampOff(o.x, o.y, next, w, h));
        return next;
      });
    }
    lastPinchDist.current = dist;
  };
  const onTouchEnd = () => { lastPinchDist.current = null; };

  /* ── canvas export ── */
  const handleConfirm = () => {
    const outputH = Math.round(outputSize * (cropH / cropW));
    const canvas = document.createElement('canvas');
    canvas.width = outputSize;
    canvas.height = outputH;
    const ctx = canvas.getContext('2d')!;
    const { w, h } = naturalRef.current;
    const iw = w * scale;
    const ih = h * scale;
    const imgLeft = cropW / 2 - iw / 2 + offset.x;
    const imgTop = cropH / 2 - ih / 2 + offset.y;
    ctx.drawImage(
      imgRef.current!,
      -imgLeft / scale, -imgTop / scale,
      cropW / scale, cropH / scale,
      0, 0, outputSize, outputH,
    );
    onConfirm(canvas.toDataURL('image/png'));
  };

  /* ── layout ── */
  const { w, h } = naturalRef.current;
  const iw = w * scale;
  const ih = h * scale;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onCancel(); }}>
      <DialogContent
        className="w-[calc(100vw-24px)] sm:max-w-xl md:max-w-2xl rounded-3xl p-0 overflow-hidden border border-slate-200 bg-white shadow-2xl"
        onInteractOutside={e => e.preventDefault()}
      >
        <DialogHeader className="px-5 sm:px-6 pt-5 sm:pt-6 pb-3 sm:pb-4 border-b bg-gradient-to-r from-slate-50 to-white">
          <DialogTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
            {title}
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            Drag to reposition · scroll or pinch to zoom
          </p>
        </DialogHeader>

        <div className="flex flex-col items-center gap-5 px-5 sm:px-6 py-5 sm:py-6 bg-white">
          <div className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-6">
            <div className="flex flex-col items-center gap-4">

              {/* ── Crop preview ── */}
              <div
                ref={cropContainerRef}
                className="relative overflow-hidden select-none touch-none flex-shrink-0"
                style={{
                  width: cropW,
                  height: cropH,
                  borderRadius: '16px',
                  background: '#94a3b8',
                  cursor: 'grab',
                }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerLeave={onPointerUp}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                {/* Crosshair */}
                <div className="absolute inset-0 pointer-events-none z-10">
                  <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/40 -translate-x-px" />
                  <div className="absolute top-1/2 left-0 right-0 h-px bg-white/40 -translate-y-px" />
                </div>

                <img
                  ref={imgRef}
                  src={imageUrl}
                  alt=""
                  draggable={false}
                  style={{
                    position: 'absolute',
                    width: imgLoaded ? iw : 0,
                    height: imgLoaded ? ih : 0,
                    left: imgLoaded ? cropW / 2 - iw / 2 + offset.x : -9999,
                    top: imgLoaded ? cropH / 2 - ih / 2 + offset.y : -9999,
                    opacity: imgLoaded ? 1 : 0,
                    userSelect: 'none',
                    pointerEvents: 'none',
                    maxWidth: 'none',
                    maxHeight: 'none',
                  }}
                />

                {/* Spinner while loading */}
                {!imgLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center z-20">
                    <div className="h-8 w-8 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                  </div>
                )}
              </div>

              {/* ── Zoom controls ── */}
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={() => changeZoom(-0.15)}
                  disabled={!imgLoaded}
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 transition-colors shadow-sm disabled:opacity-40"
                >
                  <ZoomOut className="h-4 w-4" />
                </button>
                <input
                  type="range"
                  min={Math.round(minScale * 100)}
                  max={500}
                  step={1}
                  value={Math.round(scale * 100)}
                  disabled={!imgLoaded}
                  onChange={e => {
                    const { w, h } = naturalRef.current;
                    const cover = getCoverScale(w, h);
                    const next = Math.max(cover, Math.min(5, Number(e.target.value) / 100));
                    scaleRef.current = next;
                    setScale(next);
                    setOffset(o => clampOff(o.x, o.y, next, w, h));
                  }}
                  className="w-40 sm:w-56 accent-blue-600 cursor-pointer disabled:opacity-40"
                />
                <button
                  onClick={() => changeZoom(0.15)}
                  disabled={!imgLoaded}
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 transition-colors shadow-sm disabled:opacity-40"
                >
                  <ZoomIn className="h-4 w-4" />
                </button>
                <button
                  onClick={resetTransform}
                  disabled={!imgLoaded}
                  title="Reset"
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 transition-colors shadow-sm disabled:opacity-40"
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
              </div>

              <p className="text-[11px] text-slate-400 text-center">
                Position your image inside the frame, then click Apply.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="px-5 sm:px-6 py-4 border-t bg-slate-50 flex flex-row gap-2 justify-end">
          <Button variant="outline" size="sm" onClick={onCancel} className="gap-1.5">
            <X className="h-3.5 w-3.5" /> Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={!imgLoaded}
            className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Check className="h-3.5 w-3.5" /> Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ACCOUNT SETTINGS PAGE
// ─────────────────────────────────────────────────────────────────────────────

const AccountSettings = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ── profile state ────────────────────────────────────────────────────────
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    license_number: '',
    countryCode: '+1',
    phone_number: '',
    timezone: '',
    avatar: '' as string,
    avatarFile: null as File | null,
    company_logo: '' as string,
    companyLogoFile: null as File | null,
  });

  const countryOptions = [
    { label: 'US / Canada (+1)', value: '+1' },
    { label: 'United Kingdom (+44)', value: '+44' },
    { label: 'Australia (+61)', value: '+61' },
    { label: 'India (+91)', value: '+91' },
    { label: 'Germany (+49)', value: '+49' },
  ];

  const getLocalPhoneDigits = (value: string, countryCode = '+1'): string => {
    const trimmed = value.trim();
    let digits = value.replace(/\D/g, '');
    const countryDigits = countryCode.replace(/\D/g, '');
    if (countryDigits && (trimmed.startsWith('+') || digits.length > 10) && digits.startsWith(countryDigits)) {
      digits = digits.slice(countryDigits.length);
    }
    return digits.slice(0, 10);
  };

  const formatPhoneNumber = (value: string, countryCode = '+1'): string => {
    const digits = getLocalPhoneDigits(value, countryCode);
    if (countryCode === '+1') {
      if (digits.length <= 3) return digits ? `(${digits}` : '';
      if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
    return digits;
  };

  const parsePhoneNumber = (rawPhone: string) => {
    const trimmed = rawPhone?.trim() || '';
    let countryCode = '+1';
    let digits = trimmed.replace(/\D/g, '');
    if (trimmed.startsWith('+')) {
      const matchedCountry = [...countryOptions]
        .sort((a, b) => b.value.length - a.value.length)
        .find((country) => trimmed.startsWith(country.value));
      if (matchedCountry) {
        countryCode = matchedCountry.value;
        digits = trimmed.slice(matchedCountry.value.length).replace(/\D/g, '');
      }
    }
    return { countryCode, phone: digits.slice(0, 10) };
  };

  const [initialProfile, setInitialProfile] = useState<any | null>(null);
  const [isProfileDirty, setIsProfileDirty] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [companyLogoPreview, setCompanyLogoPreview] = useState<string>('');
  const { timezone: currentTimezone, setTimezone } = useTimezone();
  const [timezones, setTimezones] = useState<string[]>([]);
  const timezoneOptions = useMemo(
    () =>
      Array.from(
        new Set([profile.timezone, ...timezones].filter((tz): tz is string => Boolean(tz?.trim())))
      ),
    [profile.timezone, timezones]
  );
  const [tzLoading, setTzLoading] = useState(false);
  const [profileErrors, setProfileErrors] = useState<{ [key: string]: string }>({});
  const [profileSaving, setProfileSaving] = useState(false);

  // ── crop modal state ─────────────────────────────────────────────────────
  const [cropModal, setCropModal] = useState<{
    open: boolean;
    imageUrl: string;
    title: string;
    target: 'avatar' | 'logo';
  }>({ open: false, imageUrl: '', title: '', target: 'avatar' });

  // ── subscription state ───────────────────────────────────────────────────
  const [plan, setPlan] = useState<any>({ is_trialing: false });
  const [planLoading, setPlanLoading] = useState(true);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pauseLoading, setPauseLoading] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const cancellationScheduled = !!plan?.cancel_at_period_end;

  // ── security state ───────────────────────────────────────────────────────
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_new_password: ''
  });
  const [passwordErrors, setPasswordErrors] = useState<{ [key: string]: string }>({});

  // ── tab state ────────────────────────────────────────────────────────────
  const searchParams = new URLSearchParams(location.search);
  const initialTab = (location.state as any)?.tab || searchParams.get('tab') || 'profile';
  const [currentTab, setCurrentTab] = useState<string>(initialTab);
  const [mobileMenuToggle, setMobileMenuToggle] = useState<(() => void) | null>(null);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [deactivateLoading, setDeactivateLoading] = useState(false);
  const [scheduledCampaigns, setScheduledCampaigns] = useState<any[]>([]);
  const [checkingCampaigns, setCheckingCampaigns] = useState(false);
  // ── fetch plan ───────────────────────────────────────────────────────────
  const fetchUserPlan = async () => {
    try {
      setPlanLoading(true);
      const userRes = await axiosInstance.get(endpoints.me);
      const user = userRes.data;
      if (!user.plan) { setPlan(null); return; }

      const plansRes = await axiosInstance.get(endpoints.plans);
      const matchedPlan = plansRes.data.find((p: any) => p.id === user.plan);

      let subData: {
        interval?: string;
        next_billing_date?: string | null;
        pause_collection?: any;
        is_trialing?: boolean;
        cancel_at_period_end?: boolean;
      } = { interval: "month", next_billing_date: null, pause_collection: null, is_trialing: false, cancel_at_period_end: false };

      try {
        const subRes = await axiosInstance.get(endpoints.subscription_info);
        subData = subRes.data;
      } catch (err) { console.warn("Subscription info not available"); }

      const isPaused = subData.pause_collection?.behavior === "mark_uncollectible";
      const nextBillingDate = user.plan_expired || subData.next_billing_date || null;
      const isTrialing = Boolean(subData.is_trialing || user.is_in_active_trial || user.is_in_free_trial);
      const isOneTimePlan = Boolean(
        matchedPlan?.is_one_time ||
        matchedPlan?.billing_cycle === "one_time" ||
        /one[-_ ]?time/i.test(matchedPlan?.name || "")
      );
      const freeOneTimeActive = Boolean(
        isOneTimePlan &&
        nextBillingDate &&
        !Number.isNaN(Date.parse(String(nextBillingDate))) &&
        Date.parse(String(nextBillingDate)) > Date.now() &&
        !user.stripe_subscription_id &&
        !user.stripe_payment_intent_id
      );
      const baseLaunchTotal = user.plan_period_total ?? user.get_total_campaign_limit ?? matchedPlan.Campaigns ?? 0;
      const baseLaunchRemaining = user.plan_period_remaining ?? user.get_current_period_remaining ?? user.get_remaining_campaigns ?? (matchedPlan.Campaigns - (user.campaigns_sent_this_month || 0));
      const extraLaunchCredits = Math.max(0, Number(user.extra_launch_credits || 0));
      const extraHubCredits = Math.max(0, Number(user.extra_hubs_credit || 0));
      const rolloverCredits = Math.max(0, Number(user.rollover_credits || 0));
      const rawTestCampaigns = Math.max(0, Number(user.test_campaigns || 0));
      const testCampaignsExpiresAt = user.test_campaigns_expires_at ? new Date(user.test_campaigns_expires_at) : null;
      const validTestCampaigns = rawTestCampaigns > 0 && (!testCampaignsExpiresAt || testCampaignsExpiresAt >= new Date())
        ? rawTestCampaigns
        : 0;
      const baseLaunchIncludesRolloverAndTest =
        user.plan_period_remaining === undefined &&
        user.get_current_period_remaining === undefined &&
        user.get_remaining_campaigns !== undefined;
      const totalAvailableLaunches = baseLaunchRemaining + extraLaunchCredits + (baseLaunchIncludesRolloverAndTest ? 0 : rolloverCredits + validTestCampaigns);

      setPlan({
        plan_name: matchedPlan.name,
        price: matchedPlan.price,
        features: matchedPlan.features?.filter((f: any) => f.status)?.map((f: any) => f.name) || [],
        is_active: !isPaused,
        is_trialing: isTrialing,
        billing_cycle: subData.interval || matchedPlan.billing_cycle || "month",
        next_billing_date: nextBillingDate,
        cancel_at_period_end: !!subData.cancel_at_period_end,
        is_one_time: isOneTimePlan,
        is_free_active: freeOneTimeActive,
        pause_count: user.pause_count || 0,
        total_campaigns: baseLaunchTotal,
        campaigns_sent_this_month: user.campaigns_sent_this_month || 0,
        remaining_campaigns: baseLaunchRemaining,
        rollover_credits: rolloverCredits,
        test_campaigns_remaining: validTestCampaigns,
        extra_launch_credits: extraLaunchCredits,
        total_available_launches: totalAvailableLaunches,
        extra_hubs_credit: extraHubCredits,
        trial_campaigns_limit: user.trial_campaigns_limit ?? 2,
        trial_campaigns_sent: user.trial_campaigns_sent ?? 0,
        remaining_trial_campaigns: user.remaining_trial_campaigns ?? 0,
      });
    } catch (err) {
      console.error(err);
      setPlan(null);
    } finally {
      setPlanLoading(false);
    }
  };

  // ── fetch profile ────────────────────────────────────────────────────────
  const fetchProfile = async () => {
    try {
      setProfileLoading(true);
      const res = await axiosInstance.get(endpoints.me);
      const user = res.data?.user ?? res.data;

      const avatarUrl = typeof user.avatar === 'string'
        ? user.avatar
        : (user?.avatar?.url ?? '');

      const companyLogoUrl = typeof user.company_logo === 'string'
        ? user.company_logo
        : (user?.company_logo?.url ?? '');

      const parsedPhone = parsePhoneNumber(user.phone_number || '');

      let userTimezone = user.timezone || '';
      if (!userTimezone) {
        userTimezone = normalizeTimezone(detectUserTimezone()) || 'UTC';
      }
      userTimezone = userTimezone.trim();

      setProfile({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        email: user.email || '',
        company: user.company || '',
        license_number: normalizeOptionalText(user.license_number),
        countryCode: parsedPhone.countryCode,
        phone_number: formatPhoneNumber(parsedPhone.phone, parsedPhone.countryCode),
        timezone: userTimezone,
        avatar: avatarUrl,
        avatarFile: null,
        company_logo: companyLogoUrl,
        companyLogoFile: null,
      });

      setInitialProfile({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        email: user.email || '',
        company: user.company || '',
        license_number: normalizeOptionalText(user.license_number),
        countryCode: parsedPhone.countryCode,
        phone_number: formatPhoneNumber(parsedPhone.phone, parsedPhone.countryCode),
        timezone: userTimezone,
        avatar: avatarUrl,
        company_logo: companyLogoUrl,
      });
      setIsProfileDirty(false);

      if (avatarUrl) setAvatarPreview(avatarUrl);
      if (companyLogoUrl) setCompanyLogoPreview(companyLogoUrl);
      if (userTimezone) setTimezone(normalizeTimezone(userTimezone) || userTimezone);

    } catch (err) {
      console.error('Profile fetch error:', err);
    } finally {
      setProfileLoading(false);
    }
  };

  // ── fetch timezones ──────────────────────────────────────────────────────
  const fetchTimezones = async () => {
    try {
      setTzLoading(true);
      const res = await axiosInstance.get(endpoints.timezones);
      if (Array.isArray(res.data)) setTimezones(res.data);
      else if (res.data?.timezones) setTimezones(res.data.timezones);
    } catch (err) {
      console.warn('Failed to load timezones', err);
    } finally {
      setTzLoading(false);
    }
  };

  const checkScheduledCampaigns = async () => {
    setCheckingCampaigns(true);
    try {
      const res = await axiosInstance.get(endpoints.listCampaigns); // adjust to your actual endpoint key
      const campaigns = res.data?.results ?? res.data ?? [];
      const scheduled = campaigns.filter((c: any) => c.status === 'scheduled');
      setScheduledCampaigns(scheduled);
    } catch (err) {
      console.error('Failed to check scheduled campaigns:', err);
      setScheduledCampaigns([]);
    } finally {
      setCheckingCampaigns(false);
    }
  };

  // ── initial load ─────────────────────────────────────────────────────────
  useEffect(() => {
    fetchProfile();
    fetchTimezones();
    fetchUserPlan();
  }, []);

  // ── stripe payment verification ──────────────────────────────────────────
  useEffect(() => {
    const sessionId = new URLSearchParams(location.search).get("session");
    if (!sessionId || !sessionId.startsWith("cs_")) return;
    const verify = async () => {
      try {
        await axiosInstance.get(endpoints.verify_payment, { params: { session_id: sessionId } });
        toast.success("Welcome! Your 7-day free trial is active!");
        await fetchUserPlan();
        setCurrentTab("subscription");
        navigate("/settings?tab=subscription", { replace: true });
      } catch (err) {
        console.error("Verification failed, but plan should be active:", err);
        await fetchUserPlan();
        toast.success("Plan activated!");
        navigate("/settings?tab=subscription", { replace: true });
      }
    };
    verify();
  }, [location.search, navigate]);

  // ── profile validation ───────────────────────────────────────────────────
  const validateProfile = () => {
    const errors: { [key: string]: string } = {};
    if (!profile.firstName) errors.firstName = 'First name is required.';
    if (!profile.lastName) errors.lastName = 'Last name is required.';
    if (!profile.email) errors.email = 'Email is required.';
    const phoneDigits = getLocalPhoneDigits(profile.phone_number, profile.countryCode);
    if (profile.phone_number && phoneDigits.length !== 10) {
      errors.phone_number = 'Please enter a 10-digit phone number after the country code.';
    }
    if (!profile.timezone) errors.timezone = 'Timezone is required.';
    setProfileErrors(errors);
    const ok = Object.keys(errors).length === 0;
    if (!ok) {
      const firstKey = Object.keys(errors)[0];
      toast.error(errors[firstKey] || 'Please fix validation errors.');
    }
    return ok;
  };

  // ── profile field update ─────────────────────────────────────────────────
  const handleProfileUpdate = (field: keyof typeof profile, value: string) => {
    if (field === "firstName" || field === "lastName" || field === "company") {
      const cleanValue = value.replace(/[^a-zA-Z\s]/g, "");
      setProfile((prev) => ({ ...prev, [field]: cleanValue }));
    } else if (field === "countryCode") {
      const rawDigits = getLocalPhoneDigits(profile.phone_number, profile.countryCode);
      setProfile((prev) => ({
        ...prev,
        countryCode: value,
        phone_number: formatPhoneNumber(rawDigits, value),
      }));
    } else if (field === "phone_number") {
      setProfile((prev) => ({ ...prev, [field]: formatPhoneNumber(value, profile.countryCode) }));
    } else {
      setProfile((prev) => ({ ...prev, [field]: value }));
    }
    setIsProfileDirty(true);
  };

  // ── profile submit ───────────────────────────────────────────────────────
  const handleProfileSubmit = async () => {
    if (!validateProfile()) return;
    setProfileSaving(true);
    try {
      const formData = new FormData();
      formData.append('first_name', profile.firstName);
      formData.append('last_name', profile.lastName);
      formData.append('email', profile.email);
      formData.append('company', profile.company);
      formData.append('license_number', normalizeOptionalText(profile.license_number));
      const rawPhoneDigits = getLocalPhoneDigits(profile.phone_number, profile.countryCode);
      formData.append('phone_number', rawPhoneDigits ? `${profile.countryCode}${rawPhoneDigits}` : '');
      formData.append('timezone', profile.timezone);

      if (profile.avatar && profile.avatar.startsWith('data:')) {
        const blob = await fetch(profile.avatar).then(r => r.blob());
        formData.append('avatar', blob, 'avatar.png');
      } else if (profile.avatarFile) {
        formData.append('avatar', profile.avatarFile);
      }

      if (profile.company_logo && profile.company_logo.startsWith('data:')) {
        const blob = await fetch(profile.company_logo).then(r => r.blob());
        formData.append('company_logo', blob, 'company_logo.png');
      } else if (profile.companyLogoFile) {
        formData.append('company_logo', profile.companyLogoFile);
      }

      const res: any = await axiosInstance.put(endpoints.me, formData, {
        headers: { "Content-Type": undefined },
      });

      const user = res.data?.user ?? res.data ?? {};
      const savedTimezone = (user.timezone ?? profile.timezone).trim();
      setProfile((prev) => ({
        ...prev,
        firstName: user.first_name ?? prev.firstName,
        lastName: user.last_name ?? prev.lastName,
        email: user.email ?? prev.email,
        company: user.company ?? prev.company,
        phone_number: user.phone_number ?? prev.phone_number,
        timezone: savedTimezone || prev.timezone,
        avatar: user.avatar ?? prev.avatar,
        company_logo: user.company_logo ?? prev.company_logo,
        avatarFile: null,
        companyLogoFile: null,
      }));

      try {
        const existing = JSON.parse(localStorage.getItem('user') || '{}');
        localStorage.setItem('user', JSON.stringify({ ...existing, ...user }));
      } catch (e) { }

      if (user.avatar) setAvatarPreview(user.avatar);
      if (user.company_logo) setCompanyLogoPreview(user.company_logo);
      toast.success('Profile updated successfully!', { position: 'top-right' });

      if (savedTimezone) setTimezone(normalizeTimezone(savedTimezone) || savedTimezone);
      setInitialProfile({
        firstName: user.first_name ?? profile.firstName,
        lastName: user.last_name ?? profile.lastName,
        email: user.email ?? profile.email,
        company: user.company ?? profile.company,
        license_number: normalizeOptionalText(user.license_number),
        phone_number: user.phone_number ?? profile.phone_number,
        timezone: savedTimezone || profile.timezone,
        avatar: user.avatar ?? profile.avatar,
        company_logo: user.company_logo ?? profile.company_logo,
      });
      setIsProfileDirty(false);
    } catch (err) {
      const respData = (err as any)?.response?.data;
      let serverMsg = 'Failed to update profile.';
      if (respData) {
        if (typeof respData === 'string') {
          serverMsg = respData;
        } else if (typeof respData === 'object') {
          const parts: string[] = [];
          for (const k of Object.keys(respData)) {
            const v = (respData as any)[k];
            if (Array.isArray(v)) parts.push(v.join(', '));
            else if (typeof v === 'object') parts.push(JSON.stringify(v));
            else parts.push(String(v));
          }
          if (parts.length) serverMsg = parts.join(' | ');
        }
      }
      toast.error(String(serverMsg), { position: 'top-right' });
    } finally {
      setProfileSaving(false);
    }
  };

  // ── password validation ──────────────────────────────────────────────────
  const validatePassword = () => {
    const errors: { [key: string]: string } = {};
    if (!passwordData.old_password) errors.old_password = 'Current password is required.';
    if (!passwordData.new_password) errors.new_password = 'New password is required.';
    if (passwordData.new_password.length < 8) errors.new_password = 'Password must be at least 8 characters.';
    if (!passwordData.confirm_new_password) errors.confirm_new_password = 'Confirm password is required.';
    if (passwordData.new_password !== passwordData.confirm_new_password) errors.confirm_new_password = 'Passwords do not match.';
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── change password ──────────────────────────────────────────────────────
  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePassword()) return;
    try {
      await axiosInstance.post(endpoints.change_password, {
        old_password: passwordData.old_password,
        new_password: passwordData.new_password,
        confirm_new_password: passwordData.confirm_new_password
      });
      toast.success('Password changed successfully!', { position: 'top-right' });
      setPasswordData({ old_password: '', new_password: '', confirm_new_password: '' });
      setPasswordErrors({});
    } catch (err: any) {
      toast.error('Failed to change password.', { position: 'top-right' });
      if (err.response?.data) {
        const apiErrors = err.response.data;
        let errors: { [key: string]: string } = {};
        if (typeof apiErrors === 'object') {
          Object.keys(apiErrors).forEach(key => {
            if (Array.isArray(apiErrors[key])) errors[key] = apiErrors[key].join(' ');
            else if (typeof apiErrors[key] === 'string') errors[key] = apiErrors[key];
          });
        }
        if (apiErrors.detail) errors.general = apiErrors.detail;
        if (apiErrors.non_field_errors) {
          errors.general = Array.isArray(apiErrors.non_field_errors)
            ? apiErrors.non_field_errors.join(' ')
            : apiErrors.non_field_errors;
        }
        setPasswordErrors(errors);
      } else {
        setPasswordErrors({ general: 'Unknown error occurred.' });
      }
    }
  };

  // ── subscription handlers ────────────────────────────────────────────────
  const handlePauseSubscription = async () => {
    if (!plan?.is_active || plan?.pause_count >= 1) {
      toast.error("You have already used your one-time pause allowance.");
      setShowPauseModal(false);
      return;
    }
    try {
      setPauseLoading(true);
      const res = await axiosInstance.post(endpoints.pause_subscription);
      toast.success(res.data.message || "Subscription paused successfully!");
      setShowPauseModal(false);
      fetchUserPlan();
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to pause subscription.");
    } finally {
      setPauseLoading(false);
    }
  };

  const handleResumeSubscription = async () => {
    try {
      setResumeLoading(true);
      const res = await axiosInstance.post(endpoints.resume_subscription);
      toast.success(res.data.message || "Subscription resumed!");
      fetchUserPlan();
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to resume.");
    } finally {
      setResumeLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setCancelLoading(true);
      const res = await axiosInstance.post(endpoints.cancel);
      toast.success(res.data?.message || "Subscription cancelled successfully.");
      await fetchUserPlan();
      setCurrentTab('profile');
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to cancel subscription.");
    } finally {
      setCancelLoading(false);
    }
  };

  const handleRegisterMobileToggle = useCallback((fn: () => void) => {
    setMobileMenuToggle(() => fn);
  }, []);

  const handleDeactivate = async () => {
    setDeactivateLoading(true);
    try {
      await axiosInstance.post(endpoints.deactivate_account); // confirm this key exists in apiEndpoints
      toast.success("Your account has been deactivated.");
      localStorage.clear();
      sessionStorage.clear();
      localStorage.setItem('logout', Date.now().toString());
      navigate("/signup", { replace: true });
    } catch (error: any) {
      console.error("Account deactivation failed:", error);
      toast.error("Failed to deactivate account", {
        description: error.response?.data?.error || "Please try again.",
      });
    } finally {
      setDeactivateLoading(false);
      setShowDeactivateConfirm(false);
    }
  };
  // ── delete account ───────────────────────────────────────────────────────
  const handleDelete = async () => {
    setDeleteLoading(true);
    setIsDeleting(true);
    try {
      await axiosInstance.delete(endpoints.deleteAccount, { data: { confirm: true } });
      toast.success("Your account has been inactivate.", {
        duration: 4000,
      });
      localStorage.clear();
      sessionStorage.clear();
      localStorage.setItem('logout', Date.now().toString());
      navigate("/signup", { replace: true });
    } catch (error: any) {
      console.error("Account deletion failed:", error);
      toast.error("Failed to delete account", {
        description: error.response?.data?.error || "Please try again.",
      });
    } finally {
      setDeleteLoading(false);
      setIsDeleting(false);
      setIsOpen(false);
    }
  };

  // ── helper: open file picker and feed into crop modal ───────────────────
  const openCropModalForFile = (
    file: File,
    target: 'avatar' | 'logo',
    title: string
  ) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    if (target === 'avatar') setProfile(prev => ({ ...prev, avatarFile: file }));
    else setProfile(prev => ({ ...prev, companyLogoFile: file }));

    const reader = new FileReader();
    reader.onload = () => setCropModal({ open: true, imageUrl: reader.result as string, title, target });
    reader.readAsDataURL(file);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="flex min-h-screen bg-white">
      <Sidebar registerMobileToggle={handleRegisterMobileToggle} />

      <main className="flex-1 flex flex-col min-w-0 w-full">
        <PageHeader onMenuClick={mobileMenuToggle ?? undefined} />

        <div className="flex-1 overflow-y-auto">
          <div className="py-3 px-2 sm:py-5 sm:px-4 lg:px-6">
            <div className="max-w-5xl mx-auto">

              <Tabs value={currentTab} onValueChange={setCurrentTab}>
                {/* Tab list */}
                <TabsList className="grid w-full grid-cols-3 bg-white rounded-xl shadow-sm p-1 h-auto min-h-[48px] border mb-4 sm:mb-6">
                  {[
                    { value: 'profile', icon: <User className="h-4 w-4 flex-shrink-0" />, label: 'Profile' },
                    { value: 'subscription', icon: <Crown className="h-4 w-4 flex-shrink-0" />, label: 'Subscription' },
                    { value: 'security', icon: <Shield className="h-4 w-4 flex-shrink-0" />, label: 'Security' },
                  ].map(tab => (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      className="rounded-lg font-medium transition-all flex items-center justify-center gap-1 sm:gap-2 py-2 px-1 sm:px-3 text-xs sm:text-sm data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md"
                    >
                      {tab.icon}
                      <span className="hidden xs:inline sm:inline">{tab.label}</span>
                    </TabsTrigger>
                  ))}
                </TabsList>

                {/* ══════════════════════════════════════════════════════════
                    PROFILE TAB
                ══════════════════════════════════════════════════════════ */}
                <TabsContent value="profile">
                  <Card className="shadow-lg border-0">
                    <CardHeader className="border-b bg-gradient-to-r from-gray-50 to-white px-3 sm:px-5 lg:px-6 py-3 sm:py-4 sm:py-5">
                      <CardTitle className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
                        <User className="h-5 w-5 text-sidebar flex-shrink-0" />
                        Profile Information
                      </CardTitle>
                      <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                        Update your personal details and profile images
                      </p>
                    </CardHeader>

                    <CardContent className="p-3 sm:p-5 lg:p-8 space-y-4 sm:space-y-6 lg:space-y-8">
                      {isProfileDirty && (
                        <div className="flex items-center gap-2 px-4 py-3 rounded-lg border border-red-300 bg-red-50 text-red-800 text-sm">
                          <span className="text-red-500 text-base">⚠</span>
                          You have unsaved changes. Click <strong className="mx-1">Save Changes</strong> to save your data.
                        </div>
                      )}

                      {profileLoading ? (
                        <div className="flex min-h-[280px] sm:min-h-[360px] flex-col items-center justify-center gap-4">
                          <Loader2 className="h-8 w-8 animate-spin text-sidebar" />
                          <p className="text-sm text-muted-foreground">Loading profile details...</p>
                        </div>
                      ) : (
                        <>
                          {/* IMAGE UPLOADS */}
                          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-3 sm:p-5 lg:p-8 border-2 border-gray-200">
                            <div className="flex flex-row justify-center items-start gap-3 sm:gap-8 lg:gap-12">

                              {/* Profile picture */}
                              <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
                                <div className="flex items-center gap-1 mb-0.5">
                                  <User className="h-3.5 w-3.5 text-sidebar flex-shrink-0" />
                                  <p className="text-[10px] sm:text-xs font-bold text-gray-900 uppercase tracking-wide truncate">
                                    Profile Picture
                                  </p>
                                </div>

                                <div className="relative group">
                                  <div className="w-16 h-16 sm:w-24 sm:h-24 lg:w-32 lg:h-32 rounded-2xl overflow-hidden bg-white ring-2 sm:ring-4 ring-sidebar/20 shadow-lg flex items-center justify-center transition-all group-hover:ring-sidebar/40">
                                    {avatarPreview ? (
                                      <img src={avatarPreview} alt="Profile" className="w-full h-full object-cover" />
                                    ) : (
                                      <User className="h-7 w-7 sm:h-10 sm:w-10 lg:h-14 lg:w-14 text-gray-400" />
                                    )}
                                  </div>
                                  <div className="absolute bottom-0 right-0 bg-sidebar text-white rounded-full p-1 sm:p-1.5 shadow-lg group-hover:scale-110 transition-transform">
                                    <Camera className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5" />
                                  </div>
                                </div>

                                <div className="w-full">
                                  <label htmlFor="avatar-upload" className="cursor-pointer">
                                    <div className="flex items-center justify-center gap-1 sm:gap-2 px-2 py-1.5 sm:px-3 sm:py-2 bg-white border-2 border-sidebar/30 text-sidebar rounded-lg hover:bg-sidebar hover:text-white transition-all font-medium shadow-sm hover:shadow-md">
                                      <Upload className="h-3 w-3 sm:h-3.5 sm:w-3.5 flex-shrink-0" />
                                      <span className="text-[10px] sm:text-xs truncate">
                                        {avatarPreview ? 'Change' : 'Upload'}
                                      </span>
                                    </div>
                                  </label>
                                  <Input
                                    id="avatar-upload"
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      openCropModalForFile(file, 'avatar', 'Crop Profile Picture');
                                      e.target.value = '';
                                    }}
                                  />
                                </div>

                                <p className="text-[9px] sm:text-xs text-center text-muted-foreground leading-relaxed">
                                  200×200px min · 5MB max
                                </p>
                              </div>

                              {/* divider */}
                              <div className="w-px self-stretch bg-gradient-to-b from-transparent via-gray-300 to-transparent mx-1" />

                              {/* Company logo */}
                              <div className="flex flex-col items-center gap-2 flex-1 min-w-0">
                                <div className="flex items-center gap-1 mb-0.5">
                                  <Building2 className="h-3.5 w-3.5 text-sidebar flex-shrink-0" />
                                  <p className="text-[10px] sm:text-xs font-bold text-gray-900 uppercase tracking-wide truncate">
                                    Company Logo
                                  </p>
                                </div>

                                <div className="relative group">
                                  <div className="w-16 h-16 sm:w-24 sm:h-24 lg:w-32 lg:h-32 rounded-2xl overflow-hidden bg-white ring-2 sm:ring-4 ring-sidebar/20 shadow-lg flex items-center justify-center transition-all group-hover:ring-sidebar/40">
                                    {companyLogoPreview ? (
                                      <img src={companyLogoPreview} alt="Company Logo" className="w-full h-full object-cover" />
                                    ) : (
                                      <Building2 className="h-7 w-7 sm:h-10 sm:w-10 lg:h-14 lg:w-14 text-gray-400" />
                                    )}
                                  </div>
                                  <div className="absolute bottom-0 right-0 bg-sidebar text-white rounded-full p-1 sm:p-1.5 shadow-lg group-hover:scale-110 transition-transform">
                                    <Camera className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5" />
                                  </div>
                                </div>

                                <div className="w-full">
                                  <label htmlFor="logo-upload" className="cursor-pointer">
                                    <div className="flex items-center justify-center gap-1 sm:gap-2 px-2 py-1.5 sm:px-3 sm:py-2 bg-white border-2 border-sidebar/30 text-sidebar rounded-lg hover:bg-sidebar hover:text-white transition-all font-medium shadow-sm hover:shadow-md">
                                      <Upload className="h-3 w-3 sm:h-3.5 sm:w-3.5 flex-shrink-0" />
                                      <span className="text-[10px] sm:text-xs truncate">
                                        {companyLogoPreview ? 'Change' : 'Upload'}
                                      </span>
                                    </div>
                                  </label>
                                  <Input
                                    id="logo-upload"
                                    type="file"
                                    accept="image/jpeg,image/png,image/gif,image/svg+xml,image/webp"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      openCropModalForFile(file, 'logo', 'Crop Company Logo');
                                      e.target.value = '';
                                    }}
                                  />
                                </div>

                                <p className="text-[9px] sm:text-xs text-center text-muted-foreground leading-relaxed">
                                  Transparent bg · 5MB max
                                </p>
                              </div>
                            </div>

                            <div className="mt-3 sm:mt-5 flex items-start gap-2 rounded-md border border-yellow-400 bg-yellow-50 p-2.5 sm:p-3 text-yellow-800">
                              <p className="text-[10px] sm:text-xs leading-relaxed">
                                Please upload a profile and company image to display on template flyers.
                              </p>
                            </div>
                          </div>

                          {/* Name fields */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                            <div className="space-y-2">
                              <Label className="text-sm font-semibold text-gray-700">
                                First Name <span className="text-red-500">*</span>
                              </Label>
                              <Input
                                value={profile.firstName}
                                onChange={e => handleProfileUpdate('firstName', e.target.value)}
                                placeholder="John"
                                className="h-10 sm:h-11 border-gray-300 focus:border-sidebar focus:ring-sidebar"
                              />
                              {profileErrors.firstName && (
                                <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
                                  <span className="text-red-500">●</span> {profileErrors.firstName}
                                </p>
                              )}
                            </div>
                            <div className="space-y-2">
                              <Label className="text-sm font-semibold text-gray-700">
                                Last Name <span className="text-red-500">*</span>
                              </Label>
                              <Input
                                value={profile.lastName}
                                onChange={e => handleProfileUpdate('lastName', e.target.value)}
                                placeholder="Doe"
                                className="h-10 sm:h-11 border-gray-300 focus:border-sidebar focus:ring-sidebar"
                              />
                              {profileErrors.lastName && (
                                <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
                                  <span className="text-red-500">●</span> {profileErrors.lastName}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Email */}
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold text-gray-700">Email Address</Label>
                            <Input
                              value={profile.email}
                              disabled
                              className="h-10 sm:h-11 bg-gray-100 cursor-not-allowed border-gray-300"
                            />
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <span className="text-amber-500">⚠</span> Email address cannot be changed
                            </p>
                          </div>

                          {/* Company */}
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold text-gray-700">Company Name</Label>
                            <Input
                              value={profile.company}
                              onChange={e => handleProfileUpdate('company', e.target.value)}
                              placeholder="Your Company Name"
                              className="h-10 sm:h-11 border-gray-300 focus:border-sidebar focus:ring-sidebar"
                            />
                          </div>

                          {/* License Number */}
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold text-gray-700">
                              License Number
                            </Label>
                            <Input
                              value={profile.license_number}
                              onChange={e => handleProfileUpdate('license_number', e.target.value)}
                              placeholder="e.g. RE-123456"
                              className="h-10 sm:h-11 border-gray-300 focus:border-sidebar focus:ring-sidebar"
                            />
                            {profileErrors.license_number && (
                              <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
                                <span className="text-red-500">●</span> {profileErrors.license_number}
                              </p>
                            )}
                          </div>
                          {/* Phone & Timezone */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                            <div className="space-y-2">
                              <Label className="text-sm font-semibold text-gray-700">Phone Number</Label>
                              <div className={`flex items-center rounded-md border ${profileErrors.phone_number ? 'border-red-500' : 'border-gray-300'} overflow-hidden`}>
                                <select
                                  value={profile.countryCode}
                                  onChange={(e) => handleProfileUpdate('countryCode', e.target.value)}
                                  className="h-10 sm:h-11 min-w-[90px] sm:min-w-[130px] bg-transparent px-2 sm:px-3 text-xs sm:text-sm outline-none border-r border-gray-200 flex-shrink-0"
                                >
                                  {countryOptions.map((country) => (
                                    <option key={country.value} value={country.value}>{country.label}</option>
                                  ))}
                                </select>
                                <Input
                                  value={profile.phone_number}
                                  maxLength={20}
                                  onChange={e => handleProfileUpdate('phone_number', e.target.value)}
                                  placeholder={profile.countryCode === '+1' ? '(555) 123-4567' : 'Phone number'}
                                  className="flex-1 min-w-0 h-10 sm:h-11 border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 text-sm"
                                />
                              </div>
                              <p className="text-xs text-gray-500">Enter your phone number after the selected country code.</p>
                              {profileErrors.phone_number && (
                                <p className="text-xs text-red-500">{profileErrors.phone_number}</p>
                              )}
                            </div>

                            <div className="space-y-2">
                              <Label className="text-sm font-semibold text-gray-700">Timezone</Label>
                              <Select value={profile.timezone} onValueChange={v => handleProfileUpdate('timezone', v)}>
                                <SelectTrigger className="h-10 sm:h-11 border-gray-300 focus:border-sidebar focus:ring-sidebar">
                                  <SelectValue placeholder="Select your timezone" />
                                </SelectTrigger>
                                <SelectContent>
                                  {tzLoading ? (
                                    <div className="p-3 text-sm text-muted-foreground text-center">Loading timezones...</div>
                                  ) : (
                                    timezoneOptions.map(tz => (
                                      <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                                    ))
                                  )}
                                </SelectContent>
                              </Select>
                              {profileErrors.timezone && (
                                <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
                                  <span className="text-red-500">●</span> {profileErrors.timezone}
                                </p>
                              )}
                            </div>
                          </div>


                          {/* Save button */}
                          <div className="flex justify-end pt-4 sm:pt-6 border-t">
                            <Button
                              variant="cta"
                              onClick={handleProfileSubmit}
                              disabled={!isProfileDirty || profileSaving}
                              className={`w-full sm:w-auto px-6 sm:px-8 h-10 sm:h-11 font-semibold shadow-lg transition-all ${isProfileDirty && !profileSaving ? 'hover:shadow-xl' : 'opacity-50 cursor-not-allowed'}`}
                            >
                              {profileSaving ? (
                                <><Loader2 className="h-4 w-4 mr-2 animate-spin [animation-duration:2s]" />Saving...</>
                              ) : (
                                <><Check className="h-4 w-4 mr-2" />Save Changes</>
                              )}
                            </Button>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* ══════════════════════════════════════════════════════════
                    SUBSCRIPTION TAB
                ══════════════════════════════════════════════════════════ */}
                <TabsContent value="subscription" className="space-y-6">
                  <Card className="shadow-card border-2 border-cta/20">
                    <CardHeader className="px-4 sm:px-6">
                      <CardTitle className="text-black flex items-center gap-2 text-base sm:text-lg">
                        <Crown className="h-5 w-5 text-cta flex-shrink-0" /> Current Plan
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 sm:px-6">
                      {planLoading ? (
                        <div className="text-center py-10 sm:py-12">
                          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]" />
                          <p className="text-muted-foreground mt-3">Loading plan details...</p>
                        </div>
                      ) : plan ? (
                        <>
                          {/* Plan header */}
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-5 sm:mb-6">
                            <div>
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <h3 className="text-xl sm:text-2xl font-bold text-black">{plan.plan_name}</h3>
                                <div className="flex flex-wrap items-center gap-2">
                                  {plan.is_free_active && (
                                    <Badge className="bg-green-100 text-green-800">
                                      Free
                                    </Badge>
                                  )}
                                  <Badge className={
                                    cancellationScheduled
                                      ? "bg-orange-100 text-orange-900"
                                      : plan.is_active
                                        ? "bg-green-100 text-green-800"
                                        : "bg-red-500 text-white"
                                  }>
                                    {cancellationScheduled ? "Cancellation Scheduled" : plan.is_active ? "Active" : "Paused"}
                                  </Badge>
                                </div>
                              </div>
                              <div className="text-2xl sm:text-3xl font-bold text-black mb-1">
                                {Number(plan.price) === 0 ? (
                                  <span className="text-green-600">Free</span>
                                ) : (
                                  <>
                                    ${plan.price}<span className="text-base sm:text-lg text-muted-foreground">/{plan.billing_cycle}</span>
                                  </>
                                )}
                              </div>
                              <p className="text-xs sm:text-sm text-muted-foreground">
                                Next billing: {plan.next_billing_date ? format(new Date(plan.next_billing_date), "MMMM d, yyyy") : "N/A"}
                              </p>
                              {cancellationScheduled && plan.next_billing_date && (
                                <p className="text-xs sm:text-sm text-orange-700 mt-1">
                                  Subscription cancellation is scheduled for{' '}
                                  {!isNaN(new Date(plan.next_billing_date).getTime())
                                    ? format(new Date(plan.next_billing_date), "MMMM d, yyyy")
                                    : "N/A"}. You'll have full access until that date.
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Features */}
                          <div className="mb-5 sm:mb-6">
                            <h4 className="font-medium text-black mb-3 text-sm sm:text-base">Included Features</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {(plan.features ?? []).map((f: string, i: number) => (
                                <div key={i} className="flex items-center gap-2">
                                  <Check className="h-4 w-4 text-cta flex-shrink-0" />
                                  <span className="text-xs sm:text-sm">{f}</span>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Campaign stats */}
                          {plan?.is_trialing ? (
                            <Card className="shadow-sm border-2 border-blue-200 bg-blue-50/40 mb-5 sm:mb-6">
                              <CardHeader className="pb-2 px-4 sm:px-6">
                                <CardTitle className="flex items-center gap-2 text-sm sm:text-base text-blue-900">
                                  <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-blue-700" /> Trial Launches
                                </CardTitle>
                              </CardHeader>
                              <CardContent className="px-4 sm:px-6">
                                <div className="grid grid-cols-3 gap-3 sm:gap-6 text-center">
                                  <div>
                                    <p className="text-xs text-muted-foreground mb-1">Trial limit</p>
                                    <p className="text-2xl sm:text-3xl font-bold text-blue-800">{plan?.trial_campaigns_limit ?? 2}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-muted-foreground mb-1">Used</p>
                                    <p className="text-2xl sm:text-3xl font-bold">{plan?.trial_campaigns_sent ?? 0}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-muted-foreground mb-1">Remaining</p>
                                    <p className={`text-2xl sm:text-3xl font-bold ${(plan?.remaining_trial_campaigns ?? 0) <= 1 ? "text-destructive" : "text-blue-800"}`}>
                                      {plan?.remaining_trial_campaigns ?? 0}
                                    </p>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ) : (
                            <Card className="shadow-sm border-2 border-primary/20 bg-primary/5 mb-5 sm:mb-6">
                              <CardHeader className="pb-2 px-4 sm:px-6">
                                <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                                  <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-primary" /> Launches This Month
                                </CardTitle>
                              </CardHeader>
                              <CardContent className="px-4 sm:px-6">
                                <div className="grid grid-cols-3 gap-3 sm:gap-6 text-center">
                                  <div>
                                    <p className="text-xs text-muted-foreground mb-1">Total</p>
                                    <p className="text-2xl sm:text-3xl font-bold text-primary">{plan.total_campaigns || "Unlimited"}</p>
                                  </div>
                                  <div>
                                    <div className="flex items-center justify-center gap-1 mb-1">
                                      <p className="text-xs text-muted-foreground">Sent</p>
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Info className="w-4 h-4 text-muted-foreground hover:text-foreground cursor-help" />
                                          </TooltipTrigger>
                                          <TooltipContent className="max-w-xs" side="top">
                                            <div className="text-xs space-y-1">
                                              <p className="font-semibold">Counts:</p>
                                              <ul className="list-disc list-inside">
                                                <li>Plan launches</li>
                                                <li>Rollover credits (annual plans)</li>
                                              </ul>
                                              <p className="font-semibold mt-2">Does NOT count:</p>
                                              <ul className="list-disc list-inside">
                                                <li>Trial launch</li>
                                                <li>Extra launch credits</li>
                                                <li>Paid launch</li>
                                              </ul>
                                            </div>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    </div>
                                    <p className="text-2xl sm:text-3xl font-bold">{plan.campaigns_sent_this_month || 0}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-muted-foreground mb-1">Remaining</p>
                                    <p className={`text-2xl sm:text-3xl font-bold ${(plan.remaining_campaigns || 0) <= 10 ? "text-destructive" : "text-primary"}`}>
                                      {plan.remaining_campaigns !== undefined ? plan.remaining_campaigns : "Unlimited"}
                                    </p>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          )}

                          <Card className="shadow-sm border border-slate-200 bg-white mb-5 sm:mb-6">
                            <CardHeader className="pb-2 px-4 sm:px-6">
                              <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                                <Rocket className="h-4 w-4 sm:h-5 sm:w-5 text-cta" /> Extra Credit Balance
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="px-4 sm:px-6">
                              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
                                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                                    <Calendar className="h-3.5 w-3.5" />
                                    Remaining plan Launches
                                  </div>
                                  <p className="text-2xl font-bold text-slate-900">
                                    {plan.remaining_campaigns ?? 0}
                                  </p>
                                </div>
                                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                                    <Repeat2 className="h-3.5 w-3.5" />
                                    Rollover launch credits
                                  </div>
                                  <p className="text-2xl font-bold text-slate-900">
                                    {plan.rollover_credits ?? 0}
                                  </p>
                                </div>
                                {/* <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                                    <Shield className="h-3.5 w-3.5" />
                                    Test campaign credits
                                  </div>
                                  <p className="text-2xl font-bold text-slate-900">
                                    {plan.test_campaigns_remaining ?? 0}
                                  </p>
                                </div> */}
                                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                                    <Rocket className="h-3.5 w-3.5" />
                                    Refund launch credits
                                  </div>
                                  <p className="text-2xl font-bold text-cta">
                                    {plan.extra_launch_credits ?? 0}
                                  </p>
                                </div>
                                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                                    <Database className="h-3.5 w-3.5" />
                                    Refund hub credits
                                  </div>
                                  <p className="text-2xl font-bold text-slate-900">
                                    {plan.extra_hubs_credit ?? 0}
                                  </p>
                                </div>
                              </div>
                              <div className="mt-3 rounded-lg border border-cta/20 bg-cta/5 px-4 py-3">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                                  <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium text-slate-900">
                                      Total launches available now
                                    </p>

                                    <div className="group relative">
                                      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-300 text-xs font-semibold text-slate-600 transition-all duration-200 hover:bg-slate-400">
                                        i
                                      </div>

                                      <div className="pointer-events-none absolute left-0 top-7 z-20 w-72 rounded-lg border bg-white p-3 text-xs text-slate-600 shadow-lg opacity-0 translate-y-1 transition-all duration-200 group-hover:opacity-100 group-hover:translate-y-0">
                                        <div className="font-medium text-slate-900 mb-1">
                                          Launch calculation
                                        </div>
                                        Remaining plan launches + Rollover launch credits + Refund launch credits
                                      </div>
                                    </div>
                                  </div>

                                  <p className="text-2xl font-bold text-cta">
                                    {plan.total_available_launches ??
                                      (
                                        (plan.remaining_campaigns ?? 0) +
                                        (plan.rollover_credits ?? 0) +
                                        (plan.extra_launch_credits ?? 0)
                                      )}
                                  </p>
                                </div>

                                <p className="text-xs text-muted-foreground mt-1">
                                  Extra launch and hub credits carry with you when your plan changes.
                                </p>
                              </div>
                            </CardContent>
                          </Card>

                          {plan?.is_trialing && (
                            <div className="mb-5 sm:mb-6 p-4 sm:p-5 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl">
                              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                                <Badge variant="secondary" className="animate-pulse text-blue-800 bg-blue-100 text-sm px-3 py-1">
                                  🎉 7-day free trial active
                                </Badge>
                                <span className="text-blue-900 font-medium text-xs sm:text-sm">
                                  You're all set — you won't be charged until your trial ends.
                                </span>
                              </div>
                              <p className="text-xs text-blue-700 ml-0 sm:ml-1">
                                Your free trial includes{" "}
                                <span className="font-semibold">{plan?.trial_campaigns_limit ?? 2} launches</span>{" "}
                                (<span className="font-semibold">{plan?.remaining_trial_campaigns ?? 0}</span> remaining).{" "}
                                After the trial ends, your{" "}
                                <span className="font-semibold">{plan?.plan_name}</span>{" "}
                                monthly credits ({plan?.total_campaigns || "Unlimited"} launches) will be available.
                              </p>
                            </div>
                          )}

                          {/* Action buttons */}
                          <div className="flex flex-wrap gap-2 sm:gap-3 mt-4 sm:mt-6">
                            <Button variant="cta" className="flex-1 sm:flex-none text-sm" onClick={() => setShowPricingModal(true)}>
                              Upgrade Plan
                            </Button>

                            {!plan?.is_active && !plan?.is_trialing && (
                              <Button
                                variant="cta"
                                className="flex-1 sm:flex-none text-sm bg-green-600 hover:bg-green-700"
                                onClick={handleResumeSubscription}
                                disabled={resumeLoading}
                              >
                                {resumeLoading ? "Resuming..." : "Resume Subscription"}
                              </Button>
                            )}

                            <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="destructive"
                                  className="flex-1 sm:flex-none text-sm"
                                  disabled={cancelLoading || cancellationScheduled}
                                >
                                  {cancellationScheduled
                                    ? "Cancellation Scheduled"
                                    : cancelLoading
                                      ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Cancelling...</>
                                      : "Cancel Subscription"}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="mx-3 sm:mx-auto max-w-[calc(100vw-24px)] sm:max-w-lg rounded-xl">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-destructive">Cancel Subscription?</AlertDialogTitle>
                                  <AlertDialogDescription className="space-y-4">
                                    <p>This action will take effect at the end of your current billing period. You'll retain access until then.</p>
                                    <div className="mt-4">
                                      <p className="font-medium text-foreground mb-2">After cancellation you will immediately lose access to:</p>
                                      <ul className="list-disc pl-5 space-y-1.5 text-sm text-muted-foreground">
                                        <li>Launching new Launches</li>
                                        <li>Access to the Hub (templates, resources, and tools)</li>
                                        <li>Any premium features tied to your current plan</li>
                                      </ul>
                                    </div>
                                    <p className="text-sm text-muted-foreground mt-4">You can reactivate anytime before the billing period ends.</p>
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2">
                                  <AlertDialogCancel className="mt-0">Keep My Plan</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={handleCancelSubscription}
                                    className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                                    disabled={cancelLoading || cancellationScheduled}
                                  >
                                    {cancelLoading ? "Cancelling..." : "Yes, Cancel Subscription"}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-10 sm:py-12">
                          <p className="text-muted-foreground mb-4">No active plan</p>
                          <Button variant="cta" onClick={() => setShowPricingModal(true)}>View Plans</Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* ══════════════════════════════════════════════════════════
                    SECURITY TAB
                ══════════════════════════════════════════════════════════ */}
                <TabsContent value="security" className="space-y-4 sm:space-y-6">
                  <Card className="shadow-card">
                    <CardHeader className="px-4 sm:px-6">
                      <CardTitle className="text-black flex items-center gap-2 text-base sm:text-lg">
                        <Shield className="h-5 w-5 flex-shrink-0" /> Password & Security
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-4 sm:px-6 space-y-5">
                      <form className="space-y-4 sm:space-y-5" onSubmit={handleChangePassword}>
                        {[
                          { id: 'currentPassword', label: 'Current Password', field: 'old_password', show: showOldPassword, toggle: () => setShowOldPassword(v => !v) },
                          { id: 'newPassword', label: 'New Password', field: 'new_password', show: showNewPassword, toggle: () => setShowNewPassword(v => !v) },
                          { id: 'confirmPassword', label: 'Confirm New Password', field: 'confirm_new_password', show: showConfirmPassword, toggle: () => setShowConfirmPassword(v => !v) },
                        ].map(({ id, label, field, show, toggle }) => (
                          <div key={id}>
                            <Label htmlFor={id} className="text-sm">{label}</Label>
                            <div className="relative mt-1.5">
                              <Input
                                id={id}
                                type={show ? "text" : "password"}
                                value={(passwordData as any)[field]}
                                onChange={e => setPasswordData({ ...passwordData, [field]: e.target.value })}
                                className="pr-10 h-10 sm:h-11"
                              />
                              <button
                                type="button"
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                onClick={toggle}
                              >
                                {show ? <EyeOff size={18} /> : <Eye size={18} />}
                              </button>
                            </div>
                            {(passwordErrors as any)[field] && (
                              <p className="text-xs text-red-500 mt-1">{(passwordErrors as any)[field]}</p>
                            )}
                          </div>
                        ))}

                        {passwordErrors.general && (
                          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                            <p className="text-sm text-red-600">{passwordErrors.general}</p>
                          </div>
                        )}

                        <Button type="submit" variant="cta" className="w-full sm:w-auto">
                          Update Password
                        </Button>
                      </form>
                    </CardContent>
                  </Card>

                  <Card className="shadow-card">
                    {/* <CardHeader className="px-4 sm:px-6">
                      <CardTitle className="text-black text-base sm:text-lg">Danger Zone</CardTitle>
                    </CardHeader> */}
                    <CardContent className="px-4 sm:px-6 py-4 sm:py-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Deactivate Account */}
                        <div className="p-4 sm:p-6 border-2 border-primary/30 rounded-lg bg-primary/5">
                          <h4 className="font-semibold text-primary mb-2 text-sm sm:text-base">Deactivate Account</h4>
                          <p className="text-xs sm:text-sm text-muted-foreground mb-4 sm:mb-6">
                            Temporarily deactivate your account. You can reactivate by logging back in.
                          </p>
                          <AlertDialog
                            open={showDeactivateConfirm}
                            onOpenChange={setShowDeactivateConfirm}
                          >
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={checkingCampaigns}
                              className="w-full sm:w-auto border-primary bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground disabled:opacity-70"
                              onClick={async () => {
                                await checkScheduledCampaigns();
                                setShowDeactivateConfirm(true);
                              }}
                            >
                              {checkingCampaigns ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Deactivating...
                                </>
                              ) : (
                                "Deactivate Account"
                              )}
                            </Button>

                            <AlertDialogContent className="mx-3 sm:mx-auto max-w-[calc(100vw-24px)] sm:max-w-lg rounded-2xl p-0 overflow-hidden border-0 shadow-2xl">

                              {scheduledCampaigns.length > 0 ? (
                                // ── Blocked state: has scheduled campaigns ─────────────
                                <>
                                  <div className="px-5 sm:px-6 pt-6 pb-4 border-primary bg-primary text-primary-foreground">
                                    <div className="flex items-start gap-3">
                                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                                        <AlertTriangle className="h-5 w-5 text-white" />
                                      </div>
                                      <div>
                                        <AlertDialogTitle className="text-base font-bold text-white">
                                          You have scheduled launches
                                        </AlertDialogTitle>
                                        <p className="text-xs text-white mt-1">
                                          <span className="font-semibold">{scheduledCampaigns.length}</span>{' '}
                                          launch{scheduledCampaigns.length > 1 ? 'es' : ''} {scheduledCampaigns.length > 1 ? 'are' : 'is'} scheduled to send
                                        </p>
                                      </div>
                                    </div>
                                  </div>

                                  <AlertDialogDescription asChild>
                                    <div className="px-5 sm:px-6 py-5 space-y-3">
                                      <div className="flex items-start gap-3 p-3.5 rounded-xl border border-slate-200 bg-slate-50">
                                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center mt-0.5">
                                          <Clock className="h-4 w-4 text-slate-600" />
                                        </div>
                                        <div>
                                          <p className="text-sm font-semibold text-gray-900">Wait it out</p>
                                          <p className="text-xs text-muted-foreground mt-0.5">
                                            Let your scheduled launches send first, then come back to deactivate your account.
                                          </p>
                                        </div>
                                      </div>

                                      <div className="flex items-start gap-3 p-3.5 rounded-xl border border-slate-200 bg-slate-50">
                                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center mt-0.5">
                                          <XCircle className="h-4 w-4 text-slate-600" />
                                        </div>
                                        <div className="flex-1">
                                          <p className="text-sm font-semibold text-gray-900">Deactivate right away</p>
                                          <p className="text-xs text-muted-foreground mt-0.5">
                                            Cancel all scheduled launches first, then your account can be deactivated immediately.
                                          </p>
                                        </div>
                                      </div>

                                      <button
                                        type="button"
                                        onClick={() => {
                                          setShowDeactivateConfirm(false);
                                          navigate('/launches');
                                        }}
                                        className="w-full flex items-center justify-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 pt-1"
                                      >
                                        Go to Launch Management <ArrowRight className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                  </AlertDialogDescription>

                                  <AlertDialogFooter className="px-5 sm:px-6 py-4 bg-slate-50 border-t border-slate-200">
                                    <AlertDialogCancel className="w-full sm:w-auto mt-0 border-primary bg-primary text-primary-foreground hover:border-primary hover:bg-primary hover:text-primary-foreground">
                                      Got it
                                    </AlertDialogCancel>
                                  </AlertDialogFooter>
                                </>

                              ) : (
                                // ── Normal confirm state ────────────────────────────────
                                <>
                                  <div className="px-5 sm:px-6 pt-6 pb-4 border-primary bg-primary text-primary-foreground">
                                    <div className="flex items-start gap-3">
                                      <AlertDialogTitle className="text-base font-bold text-white mt-1.5">
                                        Are you sure you want to deactivate?
                                      </AlertDialogTitle>
                                    </div>
                                  </div>

                                  <AlertDialogDescription asChild>
                                    <div className="px-5 sm:px-6 py-5">
                                      <p className="text-sm text-muted-foreground">
                                        Your account will be deactivated and you'll be logged out. You can reactivate anytime by simply logging back in.
                                      </p>
                                    </div>
                                  </AlertDialogDescription>

                                  <AlertDialogFooter className="px-5 sm:px-6 py-4 bg-slate-50 border-t flex-col-reverse sm:flex-row gap-2">
                                    <AlertDialogCancel disabled={deactivateLoading} className="mt-0">Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={handleDeactivate}
                                      disabled={deactivateLoading || checkingCampaigns}
                                      className="bg-primary/90"
                                    >
                                      {deactivateLoading ? "Deactivating..." : "Yes, deactivate"}
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </>
                              )}

                            </AlertDialogContent>
                          </AlertDialog>
                        </div>

                        {/* Delete Account */}
                        <div className="p-4 sm:p-6 border-2 border-destructive/30 rounded-lg bg-destructive/5">
                          <h4 className="font-semibold text-destructive mb-2 text-sm sm:text-base">Delete Account</h4>
                          <p className="text-xs sm:text-sm text-muted-foreground mb-4 sm:mb-6">
                            Permanently delete your account and all associated data. This action cannot be undone.
                          </p>
                          <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
                            <AlertDialogTrigger asChild>
                              <Button variant="destructive" size="sm" className="w-full sm:w-auto">
                                Delete My Account
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="mx-3 sm:mx-auto max-w-[calc(100vw-24px)] sm:max-w-lg rounded-xl">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-destructive">Are you absolutely sure?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This action <strong>cannot be undone</strong>. This will permanently delete:
                                  <ul className="mt-3 space-y-1 text-sm list-disc list-inside">
                                    <li>Your profile and account</li>
                                    <li>All launches, templates, and data</li>
                                    <li>Your subscription and billing info</li>
                                    <li>Everything connected to your account</li>
                                  </ul>
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2">
                                <AlertDialogCancel disabled={deleteLoading} className="mt-0">Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={handleDelete}
                                  disabled={deleteLoading}
                                  className="bg-destructive hover:bg-destructive/90"
                                >
                                  {deleteLoading ? (
                                    <><div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />Deleting...</>
                                  ) : (
                                    "Yes, delete my account"
                                  )}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>


                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>

              {/* ── MODALS ─────────────────────────────────────────────── */}

              {/* Image crop modal */}
              <ImageCropModal
                open={cropModal.open}
                imageUrl={cropModal.imageUrl}
                title={cropModal.title}
                outputSize={400}
                cropW={cropModal.target === 'avatar' ? PROFILE_CROP_W : LOGO_CROP_W}
                cropH={cropModal.target === 'avatar' ? PROFILE_CROP_H : LOGO_CROP_H}
                onConfirm={(croppedUrl) => {
                  if (cropModal.target === 'avatar') {
                    setAvatarPreview(croppedUrl);
                    setProfile(prev => ({ ...prev, avatar: croppedUrl }));
                  } else {
                    setCompanyLogoPreview(croppedUrl);
                    setProfile(prev => ({ ...prev, company_logo: croppedUrl }));
                  }
                  setIsProfileDirty(true);
                  setCropModal(prev => ({ ...prev, open: false }));
                }}
                onCancel={() => {
                  if (cropModal.target === 'avatar') setProfile(prev => ({ ...prev, avatarFile: null }));
                  else setProfile(prev => ({ ...prev, companyLogoFile: null }));
                  setCropModal(prev => ({ ...prev, open: false }));
                }}
              />

              {/* Pause modal */}
              <Dialog open={showPauseModal} onOpenChange={setShowPauseModal}>
                <DialogContent className="mx-3 sm:mx-auto max-w-[calc(100vw-24px)] sm:max-w-md rounded-xl">
                  <DialogHeader>
                    <DialogTitle>Pause Your Subscription</DialogTitle>
                    <DialogDescription className="space-y-3">
                      <p>You are about to use your <strong>one-time pause allowance</strong>:</p>
                      <p className="text-lg font-semibold text-primary">
                        {plan?.plan_name?.includes("Pro") ? "60 days" : "30 days"}
                      </p>
                      <p>Your subscription will be paused immediately. Resume anytime — <strong>only actual paused days added</strong>.</p>
                      <p className="text-sm text-destructive">This is a one-time benefit. You cannot pause again.</p>
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
                    <Button variant="outline" onClick={() => setShowPauseModal(false)} disabled={pauseLoading} className="mt-0">
                      Cancel
                    </Button>
                    <Button variant="destructive" onClick={handlePauseSubscription} disabled={pauseLoading}>
                      {pauseLoading ? "Pausing..." : "Pause Subscription"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Pricing modal */}
              <Dialog open={showPricingModal} onOpenChange={setShowPricingModal}>
                <DialogContent className="w-[calc(100vw-16px)] sm:max-w-screen-2xl max-h-[92vh] overflow-y-auto p-0 rounded-xl">
                  <div className="p-4 sm:p-6 pt-0">
                    <Pricing inModal={true} />
                  </div>
                </DialogContent>
              </Dialog>

            </div>
          </div>
        </div>

        <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 flex flex-col gap-2 sm:gap-3 z-50">
          <BotLauncher />
        </div>
        {/* Full Page Loader Overlay during Account Deletion */}
        {isDeleting && (
          <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center">
            <div className="flex flex-col items-center">
              <Loader2 className="h-12 w-12 animate-spin text-white" />
              <p className="text-white text-lg font-medium mt-4">
                Deleting your account...
              </p>
              <p className="text-white/70 text-sm mt-1">
                This may take a few moments
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AccountSettings;
